import Box from "@mui/material/Box";
import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import {
  ADD_ADDRESS,
  GET_ADDRESS,
  GET_DEFAULT_ADDRESS,
  UPDATE_ADDRESS,
} from "graphql/address";
import { useEffect, useState } from "react";
import {
  addressInitialValues,
  buildAddAddressVariables,
  buildUpdateAddressVariables,
  ConfigureAddressValidationType,
  FormikAddress,
  mapAddressToFormik,
} from "utils/addressFormat";
import {
  addressValidation,
  createLocation,
} from "validations/addressValidation";
import { useFormikHelpers } from "./useFormikHelpers";
import { toast } from "react-toastify";
import useGetLoggedInUser from "./useGetLoggedInUser";

interface UseAddressDataProps {
  addressId?: string;
  initialValues?: FormikAddress;
  loadDefaultAddress?: boolean;
  type?: ConfigureAddressValidationType;
}

export function useAddressData({
  addressId,
  initialValues,
  loadDefaultAddress,
  type = ConfigureAddressValidationType.VALIDATE,
}: UseAddressDataProps) {
  const { user } = useGetLoggedInUser();

  const [effectiveInitialValues, setEffectiveInitialValues] =
    useState<FormikAddress>({ ...addressInitialValues });

  const { data: addressData, loading: populatingAddress } = useQuery(
    GET_ADDRESS,
    {
      variables: { id: addressId },
      fetchPolicy: "cache-and-network",
      skip: !addressId,
    },
  );

  const { data: defaultAddressData, loading: loadingDefaultAddress } = useQuery(
    GET_DEFAULT_ADDRESS,
    {
      variables: { companyId: user?.companyId },
      skip: !loadDefaultAddress || !!addressId || !user?.companyId,
    },
  );

  const [updateAddress, { loading: updatingAddress }] =
    useMutation(UPDATE_ADDRESS);

  const [addAddress, { loading: creatingAddress }] = useMutation(ADD_ADDRESS);

  useEffect(() => {
    if (initialValues) {
      setEffectiveInitialValues({
        ...addressInitialValues,
        ...initialValues,
      });
      return;
    }

    if (addressData?.address) {
      setEffectiveInitialValues((prev) => ({
        ...prev,
        ...mapAddressToFormik(addressData.address),
      }));
    }

    if (defaultAddressData?.defaultAddress && !addressData?.address) {
      setEffectiveInitialValues((prev) => ({
        ...prev,
        ...mapAddressToFormik(defaultAddressData.defaultAddress),
      }));
    }
  }, [initialValues, defaultAddressData, addressData]);

  const formikAddress = useFormik<FormikAddress>({
    initialValues: effectiveInitialValues,
    validationSchema:
      type === ConfigureAddressValidationType.CREATE
        ? createLocation
        : addressValidation,
    enableReinitialize: true,
    onSubmit: async () => {},
  });

  const { validateAndTouch } = useFormikHelpers(formikAddress);

  useEffect(() => {
    if (addressData?.address) {
      formikAddress.resetForm({
        values: mapAddressToFormik(addressData.address),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData]);

  const effectiveAddressId =
    addressId || defaultAddressData?.defaultAddress?.id;

  const handleSaveAddress = async (): Promise<string | null> => {
    const isValid = await validateAndTouch();
    if (!isValid) return null;

    const formData = formikAddress.values;

    try {
      if (effectiveAddressId) {
        const updateAddressVariables = buildUpdateAddressVariables(
          effectiveAddressId,
          formData,
        );

        const { data } = await updateAddress({
          variables: updateAddressVariables,
        });
        return data?.updateAddress?.id || effectiveAddressId;
      } else {
        const addAddressVariables = buildAddAddressVariables(
          user?.companyId || "",
          formData,
        );

        const { data } = await addAddress({
          variables: {
            ...addAddressVariables,
          },
        });
        return data?.addAddress?.id || null;
      }
    } catch (error: any) {
      toast.error(error.message);
      return null;
    }
  };

  const specificLoadingStates = {
    populatingAddress,
    creatingAddress,
    updatingAddress,
    loadingDefaultAddress,
  };

  const isAnyLoading = Object.values(specificLoadingStates).some(
    (loading) => loading,
  );

  return {
    formikAddress,
    specificLoadingStates,
    isAnyLoading,
    handleSaveAddress,
    resetFormToInitialValues: () =>
      formikAddress.resetForm({ values: effectiveInitialValues }),
  };
}

export function formatAddressDisplay(
  address:
    | {
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        zipcode?: string;
      }
    | null
    | undefined,
): React.ReactNode {
  if (!address?.address1) return null;

  const cityStateZip = [address.city, address.state, address.zipcode]
    .filter(Boolean)
    .join(", ");

  // If there's no city/state/zip line, return null (no address to display)
  if (!cityStateZip) return null;

  // No alignment here: eight call sites drop this into a Summary, which
  // right-aligns a second-column field and leaves a spanning one alone. A
  // formatter that hardcodes `text-right` fights whichever container it lands in.
  return (
    <>
      <Box>{address.address1}</Box>
      {address.address2 && <Box>{address.address2}</Box>}
      <Box>{cityStateZip}</Box>
    </>
  );
}
